import { Injectable } from '@angular/core';
import {
  BASE_API_URL,
  Boondock,
  BoondockPoint,
  IAmenity,
  IBoondock,
  IBoondockPoint,
  IBoondockSearchQuery,
  IBoondockWhere,
  ICoordinateGis,
  ILandType,
  IPhoto,
  ISignalReception,
  ISubPhoto,
  IUser,
  LatLon,
  SearchResult,
  SEARCH_API_URL
} from '@curbnturf/entities';
import { isNumber, objectToUrlParams } from '@curbnturf/helpers';
import {
  BoondockDbSet,
  BoondockPhotosDbSet,
  CachedHttpClient,
  DataCacheDbSet,
  ListingDbSet,
  ListingPhoto,
  ListingPhotosDbSet,
  Logger
} from '@curbnturf/network';
import { DateTime } from 'luxon';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

const API_URL = BASE_API_URL + 'boondock';

@Injectable({
  providedIn: 'root',
})
export class BoondockService {
  constructor(
    private boondockDbSet: BoondockDbSet,
    private boondockPhotosDbSet: BoondockPhotosDbSet,
    private dataCache: DataCacheDbSet,
    private http: CachedHttpClient,
    private listingDbSet: ListingDbSet,
    private listingPhotosDbSet: ListingPhotosDbSet,
    private logger: Logger,
  ) {}

  create(boondock: IBoondock): Observable<IBoondock> {
    return this.http.post<IBoondock>(API_URL, this.formatForServer(boondock));
  }

  createAmenity(boondock: IBoondock): Observable<IBoondock> {
    const amenities: IAmenity[] = [];
    if (boondock.amenities) {
      amenities.push(...boondock.amenities);
    }

    amenities.push({ boondock });

    return this.update({ ...boondock, amenities });
  }

  createSignalReception(boondock: IBoondock): Observable<IBoondock> {
    const signalReception: ISignalReception[] = [];
    if (boondock.signalReception) {
      signalReception.push(...boondock.signalReception);
    }

    return this.update({
      ...boondock,
      signalReception,
    });
  }

  find(query: IBoondockSearchQuery, limit?: number, page?: number): Observable<SearchResult[]> {
    let queryString = `${SEARCH_API_URL}?${query.toString()}`;
    if (limit) queryString += `&limit=${limit}`;
    if (page) queryString += `&page=${limit}`;

    return this.http
      .get(queryString, undefined, {
        offlineCallback: () => [],
      })
      .pipe(
        map((data) => {
          return data.map(
            (boondock: IBoondock) =>
              new SearchResult({
                type: 'boondock',
                name: boondock.name || '',
                coordinates: boondock.coordinates as ICoordinateGis,
                mapPoint: BoondockPoint.fromBoondock(boondock),
              }),
          );
        }),
      );
  }

  fetch(boondockId: number): Observable<IBoondock> {
    return this.http.get<IBoondock>(`${API_URL}/${boondockId}`);
  }

  fetchAll(
    query: IBoondockWhere,
    sort: string = 'id',
    page: number = 1,
    pageSize: number = 100,
  ): Observable<{ total: number; page: number; boondocks: IBoondock[] }> {
    const queryString = objectToUrlParams({ ...query, sort, page, pageSize });
    return this.http.get<{ total: number; page: number; boondocks: IBoondock[] }>(`${API_URL}?${queryString}`);
  }

  remove(boondockId: number): Observable<IBoondock> {
    return this.http.delete<IBoondock>(`${API_URL}/${boondockId}`);
  }

  restore(boondockId: number): Observable<IBoondock> {
    return this.http.post<IBoondock>(`${API_URL}/${boondockId}/restore`, null);
  }

  removeAmenity(boondock: IBoondock, amenity: IAmenity): Observable<IBoondock> {
    if (!boondock.amenities) {
      return of(boondock);
    }

    const amenities = [...boondock.amenities];

    const index = amenities.indexOf(amenity);

    if (index || index === 0) {
      amenities.splice(index, 1);
    }

    return this.update({ ...boondock, amenities });
  }

  removeSignalReception(boondock: IBoondock, signalReception: ISignalReception): Observable<IBoondock> {
    if (!boondock.signalReception) {
      return of(boondock);
    }

    const signalReceptions = [...boondock.signalReception];

    const index = signalReceptions.indexOf(signalReception);

    if (index || index === 0) {
      signalReceptions.splice(index, 1);
    }

    return this.update({
      ...boondock,
      signalReception: signalReceptions,
    });
  }

  upsert(boondock: IBoondock): Observable<IBoondock> {
    if (boondock.id) {
      return this.update(boondock);
    } else {
      return this.create(new Boondock(boondock));
    }
  }

  update(boondock: IBoondock): Observable<IBoondock> {
    if (!boondock.id) {
      throw new Error('Missing Boondock Identifier');
    }
    return this.http.put<IBoondock>(`${API_URL}/${boondock.id}`, this.formatForServer(boondock));
  }

  enable(boondock: IBoondock): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${boondock.id}/enable`, null);
  }

  disable(boondock: IBoondock): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${boondock.id}/disable`, null);
  }

  removeImage(boondockId: number, photoIndex: number): Observable<IPhoto[]> {
    return this.http.delete<IPhoto[]>(`${API_URL}/${boondockId}/remove-image/${photoIndex}`);
  }

  uploadImage(boondockId: number, formData: FormData): Observable<{ id: number; photos: IPhoto[] }> {
    return this.http.post<{ id: number; photos: IPhoto[] }>(`${API_URL}/${boondockId}/upload-image`, formData);
  }

  updatePhotos(boondockId: number, photos: IPhoto[]): Observable<IBoondock> {
    if (!boondockId) {
      throw new Error('Missing boondock Identifier');
    }
    return this.http.put<IBoondock>(`${API_URL}/${boondockId}/photo-details/`, photos);
  }

  favorite(point: IBoondockPoint): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${point.id}/favorite`, null);
  }

  unfavorite(point: IBoondockPoint): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ success: boolean; message: string }>(`${API_URL}/${point.id}/unfavorite`, null);
  }

  /**
   * Retrieves owned boondock from the local SQL database
   * @param {IUser} user
   */
  async retrieveLocalOwnedListings(user: IUser): Promise<IBoondock[]> {
    const boondock = await this.boondockDbSet.retrieveOwnBoondock(user?.id);
    const boondockIds: number[] = boondock
      .filter((el: IBoondock) => el !== undefined)
      .map((el: IBoondock) => el.id as number);

    // Get the photo records
    const photoResults = await this.boondockPhotosDbSet.retrieveBatch(boondockIds);
    boondock.forEach((boondock: IBoondock) => {
      boondock.photos = [...photoResults.filter((photo: IPhoto) => photo.boondockId === boondock.id)];
    });

    return boondock;
  }

  public async preload(): Promise<void> {
    const queryString = `${SEARCH_API_URL}`;

    this.logger.debug('Preloading Listing Data');

    try {
      this.http
        .get(queryString, undefined, {
          offlineCallback: () => [],
        })
        .pipe(
          map((data) => {
            return data.map(
              (boondock: IBoondock) =>
                new SearchResult({
                  type: 'boondock',
                  name: boondock.name || '',
                  coordinates: boondock.coordinates as ICoordinateGis,
                  mapPoint: BoondockPoint.fromBoondock(boondock),
                }),
            );
          }),
        )
        .subscribe({
          next: async (results: SearchResult[]) => {
            const listingRecords: IBoondockPoint[] = [];
            const listingPhotoRecords: ListingPhoto[] = [];

            results.forEach((record: SearchResult) => {
              if (record.mapPoint) {
                listingRecords.push(
                  new BoondockPoint({
                    ...record.mapPoint,
                  }),
                );

                if (record.mapPoint.photos && Array.isArray(record.mapPoint.photos)) {
                  const photos: ISubPhoto[] = record.mapPoint.photos;
                  listingPhotoRecords.push(
                    ...photos.map((el: ISubPhoto) => ({
                      listingId: record.mapPoint?.id || 0,
                      key: el.key,
                      caption: el.caption || '',
                    })),
                  );
                }
              }
            });

            // Save the set
            await this.listingDbSet.truncateAndStoreArray(listingRecords);
            await this.listingPhotosDbSet.truncateAndStoreArray(listingPhotoRecords);

            await this.dataCache.store({
              cacheName: 'listings',
              lastModified: DateTime.now().toMillis(),
            });
          },
          error: (e) => {
            this.logger.error('Failed to Preload Listing Data', JSON.stringify(e));
          },
        });
    } catch (e) {
      this.logger.error('Failed to Preload Listing Data', JSON.stringify(e));
    }
  }

  async retrieveCachedListings(
    query: IBoondockSearchQuery,
    currentLocation: LatLon,
    take?: number,
    skip?: number,
  ): Promise<IBoondockPoint[]> {
    const results = await this.listingDbSet.retrieveFiltered(query, take, skip);

    // calculate and sort by distance from current location.
    results.sort((a, b) => {
      const aDistance = a.distance
        ? a.distance
        : currentLocation.distanceTo(new LatLon({ lat: a.coordinates.lat, lon: a.coordinates.lon }));
      a.distance = aDistance;
      const bDistance = b.distance
        ? b.distance
        : currentLocation.distanceTo(new LatLon({ lat: b.coordinates.lat, lon: b.coordinates.lon }));
      b.distance = bDistance;
      return aDistance - bDistance;
    });

    // Get the photo records
    const photoResults = await this.listingPhotosDbSet.retrieveBatch(
      results.map((el) => el.id).filter((id) => isNumber(id)) as number[],
    );
    results.forEach((listing) => {
      listing.photos = [...photoResults.filter((photo) => photo.listingId === listing.id)];
    });

    return results;
  }

  updatePropertyType(boondockId: number, types: ILandType['type'][]): Observable<IBoondock> {
    if (!boondockId) {
      throw new Error('Missing Boondock Identifier');
    }
    return this.http.put<IBoondock>(`${API_URL}/${boondockId}/property-types`, { types });
  }

  private formatForServer(boondock: IBoondock): IBoondock {
    return { ...boondock };
  }
}
